import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getRentalItems,
  deleteRentalItem,
  getRentalCategoriesMaster,
} from "../../../services/rentalService";
import { useMemo, useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import AddRental from "./AddRental";
import Dialog from "../../dialog/Dialog";
import Pagination from "../../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
} from "../../common/adminTable";
import { buildPaginationMeta } from "../../../utils/clientPagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../../common/supplierPageMeta";
import { getTableEmptyProps } from "../../common/supplierTableEmpty";
import {
  SupplierAddButton,
  SupplierFilterToolbar,
  SupplierFilterField,
  SUPPLIER_FILTER_SELECT_CLASS,
  SUPPLIER_FILTER_INPUT_CLASS,
  SupplierPageLoading,
} from "../../common/supplierPageUi";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useCloseOnSidebarNavigate } from "../../../hooks/useCloseOnSidebarNavigate";

const Rentals = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [approvalFilter, setApprovalFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [addRentalOpen, setAddRentalOpen] = useState(false);
  const [selectedRentalId, setSelectedRentalId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rentalToDeleteId, setRentalToDeleteId] = useState(null);
  const queryClient = useQueryClient();
  const location = useLocation();
  const pageMeta = SUPPLIER_PAGE_META.rentals;

  const closeAddFlow = useCallback(() => {
    setAddRentalOpen(false);
    setSelectedRentalId(null);
  }, []);

  useEffect(() => {
    closeAddFlow();
  }, [location.pathname, closeAddFlow]);

  useCloseOnSidebarNavigate(closeAddFlow);

  const { data: categoriesRes } = useQuery({
    queryKey: ["rentalCategoriesMaster"],
    queryFn: getRentalCategoriesMaster,
  });
  const categories = Array.isArray(categoriesRes?.data) ? categoriesRes.data : [];

  const filters = useMemo(() => {
    const f = {};
    if (searchQuery) f.search = searchQuery;
    if (categoryId) f.category_id = categoryId;
    if (statusFilter !== "") f.is_active = statusFilter;
    if (approvalFilter !== "") f.is_approved = approvalFilter;
    if (locationFilter) f.location = locationFilter;
    return f;
  }, [searchQuery, categoryId, statusFilter, approvalFilter, locationFilter]);

  const {
    data: rentalsData,
    isLoading: rentalsLoading,
    error: rentalsError,
  } = useQuery({
    queryKey: ["rentals", currentPage, filters],
    queryFn: () => getRentalItems(currentPage, filters),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRentalItem,
    onSuccess: () => {
      queryClient.invalidateQueries(["rentals"]);
      setDeleteDialogOpen(false);
      setRentalToDeleteId(null);
    },
    onError: (err) => {
      alert(err?.response?.data?.message || "Error deleting rental item");
    },
  });

  const rentals = Array.isArray(rentalsData?.data)
    ? rentalsData.data
    : rentalsData?.data?.data || [];
  const apiPagination = Array.isArray(rentalsData?.data) ? {} : rentalsData?.data || {};

  const pagination = buildPaginationMeta({
    currentPage: apiPagination.current_page ?? currentPage,
    lastPage: apiPagination.last_page ?? 1,
    perPage: apiPagination.per_page ?? 10,
    total: apiPagination.total ?? 0,
    from: apiPagination.from,
    to: apiPagination.to,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAddRental = () => {
    setSelectedRentalId(null);
    setAddRentalOpen(true);
  };

  const handleEdit = (rental) => {
    setSelectedRentalId(rental.id);
    setAddRentalOpen(true);
  };

  const handleDelete = (id) => {
    setRentalToDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (rentalToDeleteId) {
      deleteMutation.mutate(rentalToDeleteId);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setCategoryId("");
    setStatusFilter("");
    setApprovalFilter("");
    setLocationFilter("");
    setCurrentPage(1);
  };

  const adminReviewBadge = (rental) => {
    const ap = Number(rental.is_approved);
    const st = Number(rental.status);
    if (ap === 1) {
      return (
        <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-green-100 text-green-800">
          Approved
        </span>
      );
    }
    if (ap === 2) {
      return (
        <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-red-100 text-red-800">
          Rejected · resubmit
        </span>
      );
    }
    if (st === 1) {
      return (
        <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-900">
          Pending review
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700">
        Draft
      </span>
    );
  };

  if (addRentalOpen) {
    return (
      <AddRental
        open={addRentalOpen}
        rentalId={selectedRentalId}
        onClose={() => {
          setAddRentalOpen(false);
          setSelectedRentalId(null);
          queryClient.invalidateQueries(["rentals"]);
        }}
      />
    );
  }

  const hasActiveFilters =
    searchQuery || categoryId || statusFilter || approvalFilter || locationFilter;

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={pageMeta.title}
        subtitle={pageMeta.subtitle}
        actions={
          <SupplierAddButton onClick={handleAddRental}>Add Rental Item</SupplierAddButton>
        }
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar showReset={hasActiveFilters} onReset={clearFilters}>
            <SupplierFilterField label="Search">
              <TableSearchInput
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by title or location..."
                className="mt-1 w-full sm:w-full"
              />
            </SupplierFilterField>

            <SupplierFilterField label="Category" className="md:col-span-2">
              <select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setCurrentPage(1);
                }}
                className={SUPPLIER_FILTER_SELECT_CLASS}
              >
                <option value="">All</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </SupplierFilterField>

            <SupplierFilterField label="Active" className="md:col-span-2">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={SUPPLIER_FILTER_SELECT_CLASS}
              >
                <option value="">All</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </SupplierFilterField>

            <SupplierFilterField label="Admin review" className="md:col-span-3">
              <select
                value={approvalFilter}
                onChange={(e) => {
                  setApprovalFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={SUPPLIER_FILTER_SELECT_CLASS}
              >
                <option value="">All</option>
                <option value="0">Pending / draft</option>
                <option value="1">Approved</option>
                <option value="2">Rejected</option>
              </select>
            </SupplierFilterField>

            <SupplierFilterField label="Location" className="md:col-span-2">
              <input
                value={locationFilter}
                onChange={(e) => {
                  setLocationFilter(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="e.g. Kayamkulam"
                className={SUPPLIER_FILTER_INPUT_CLASS}
              />
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        {rentalsLoading ? (
          <SupplierPageLoading message="Loading rentals..." />
        ) : rentalsError ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="fi fi-rr-exclamation text-red-500 text-4xl mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading rentals
              </h3>
              <p className="text-gray-600">{rentalsError.message}</p>
            </div>
          </div>
        ) : (
          <AdminTable
            className="shadow-none rounded-none"
            footer={
              <AdminTableFooter>
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </AdminTableFooter>
            }
          >
            <AdminTableHead>
              <AdminTableTh>#</AdminTableTh>
              <AdminTableTh>Item</AdminTableTh>
              <AdminTableTh>Category</AdminTableTh>
              <AdminTableTh>Location</AdminTableTh>
              <AdminTableTh>Quantity</AdminTableTh>
              <AdminTableTh>Admin review</AdminTableTh>
              <AdminTableTh>Active</AdminTableTh>
              <AdminTableTh align="right">Actions</AdminTableTh>
            </AdminTableHead>
            <AdminTableBody>
              {rentals.length === 0 ? (
                <TableEmptyRow
                  colSpan={8}
                  {...getTableEmptyProps("rentals", {
                    onAdd: handleAddRental,
                    hasActiveFilters,
                    onClearFilters: clearFilters,
                  })}
                />
              ) : (
                rentals.map((rental, index) => (
                  <AdminTableRow
                    key={rental.id}
                    className={
                      Number(rental.is_approved) === 2 ? "bg-red-50/60" : ""
                    }
                  >
                    <AdminTableCell className="text-gray-500">
                      {(currentPage - 1) * pagination.perPage + index + 1}
                    </AdminTableCell>
                    <AdminTableCell className="font-medium">
                      <div className="flex items-center">
                        {rental.thumbnail_image_url ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover mr-3"
                            src={rental.thumbnail_image_url}
                            alt=""
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center mr-3 text-gray-400">
                            <i className="fi fi-rr-image" />
                          </div>
                        )}
                        <div className="text-sm font-medium text-gray-900">
                          {rental.title}
                        </div>
                      </div>
                    </AdminTableCell>
                    <AdminTableCell className="text-gray-500">
                      {rental.category?.name || "N/A"}
                    </AdminTableCell>
                    <AdminTableCell className="text-gray-500">
                      {rental.location}
                    </AdminTableCell>
                    <AdminTableCell className="text-gray-500">
                      {rental.quantity}
                    </AdminTableCell>
                    <AdminTableCell>{adminReviewBadge(rental)}</AdminTableCell>
                    <AdminTableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-[10px] font-semibold ${
                          rental.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {rental.is_active ? "Active" : "Inactive"}
                      </span>
                    </AdminTableCell>
                    <TableActionsCell>
                      <EditActionButton
                        showLabel={false}
                        onClick={() => handleEdit(rental)}
                      />
                      <DeleteActionButton
                        showLabel={false}
                        onClick={() => handleDelete(rental.id)}
                      />
                    </TableActionsCell>
                  </AdminTableRow>
                ))
              )}
            </AdminTableBody>
          </AdminTable>
        )}
      </AdminPageCard>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            Delete Rental Item
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={confirmDelete}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={deleteMutation.isPending}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this rental item? This action cannot be undone.
          </p>
        </div>
      </Dialog>
    </AdminPageLayout>
  );
};

export default Rentals;
